import React from 'react'
import ReactDOM from 'react-dom/client'
import * as Sentry from '@sentry/react'
import App from './App.tsx'
import { reportError, setupGlobalErrorHandlers } from './utils/errorReporter'
import { suppressClientMonitoringEvent } from './utils/clientErrorNoise'
import { useThemeStore } from './store/themeStore'
import { installApiInterceptor } from './lib/apiInterceptor'
import './i18n'
import './index.css'

// Patch window.fetch BEFORE any module fires its first request so all
// auth-bearing calls are covered by the global 401 → "session expired"
// → /login redirect handler. See lib/apiInterceptor.ts for details.
installApiInterceptor();

useThemeStore.getState().initTheme();

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.PROD,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 0.3,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.MODE,
  beforeSend(event) {
    const message = event.exception?.values?.[0]?.value || '';
    const stack = event.exception?.values?.[0]?.stacktrace?.frames
      ?.map((f) => `  at ${f.function || '?'} (${f.filename}:${f.lineno}:${f.colno})`)
      .reverse()
      .join('\n');

    if (suppressClientMonitoringEvent(message, stack)) return null;

    if (!message) return event;

    reportError({
      message,
      stack,
      url: event.request?.url || window.location.pathname,
      component: (event.tags?.transaction as string) || undefined,
      severity: (event.level as 'error' | 'warning' | 'fatal' | 'info') || 'error',
    });

    return event;
  },
})

setupGlobalErrorHandlers();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
